import { styled } from '@linaria/react';
import { MOBILE_MEDIA_QUERY, THEME_HUE } from '../../app/global.linaria.global';

export const MIN_NAV_HEIGHT = 90;

export const Nav = styled.nav`
	position: sticky;
	top: 0;
	left: 0;
	right: 0;
	background: rgba(10, 10, 10, 0.95);
	backdrop-filter: blur(10px);
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	z-index: 1000;
	padding: 0;
`;

export const MobileMenuButton = styled.button`
	display: none;
	background: none;
	border: none;
	font-size: 1.5rem;
	color: #ffffff;
	cursor: pointer;
	padding: 0.5rem;
	border-radius: 4px;
	transition: background-color 0.3s ease;
	max-height: 100%;
	box-sizing: border-box;

	&:hover {
		background-color: rgba(255, 255, 255, 0.1);
	}

	@media ${MOBILE_MEDIA_QUERY} {
		display: block;
	}
`;

export const NavContainer = styled.div`
	max-width: 110rem;
	margin: 0 auto;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem min(4vw, 5rem);
	min-height: ${MIN_NAV_HEIGHT}px;
	box-sizing: border-box;

	@media ${MOBILE_MEDIA_QUERY} {
		padding: 1rem;
		justify-content: space-evenly;
	}
`;

export const Logo = styled.div`
	text-decoration: none;
	color: inherit;
	font-size: min(1em, 1.4vw);

	a {
		text-decoration: none;
		color: inherit;
	}

	@media (max-width: 750px) {
		> div {
			font-size: min(1em, 8vw);
			width: 20em;
			margin: 0;
			> h1 {
				margin: 0;
			}
		}
	}

	@media (max-width: 590px) {
		display: none;
		overflow: hidden;
		width: 0;
		opacity: 0;
	}
`;

export const LogoText = styled.span`
	font-size: 1.5rem;
	font-weight: 700;
	color: #ffffff;
	background: linear-gradient(230deg, hsl(${THEME_HUE - 20.5}, 56%, 55%), hsl(${THEME_HUE + 20.5}, 100%, 65%));
	-webkit-background-clip: text;
	background-clip: text;
	-webkit-text-fill-color: transparent;
`;

export const NavLinks = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 2rem;

	&.open {
		transform: translateY(0);
		opacity: 1;
		visibility: visible;
	}

	@media ${MOBILE_MEDIA_QUERY} {
		z-index: -1;
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		background: rgba(10, 10, 10, 0.98);
		flex-direction: column;
		gap: 0;
		padding: 1rem 0;
		box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
		transform: translateY(-100%);
		opacity: 0;
		visibility: hidden;
		transition: all 0.3s ease;
	}
`;

export const NavLink = styled.div`

	--link-txt-color-noncurrent-normal: #cbd3df;
	--link-udl-color-noncurrent-normal: #0000;

	--link-txt-color-noncurrent-hover: #839aff;
	--link-udl-color-noncurrent-hover: #839affcc;

	--link-txt-color-current-normal: hsl(${THEME_HUE}, 100%, 85%);
	--link-udl-color-current-normal: hsla(${THEME_HUE}, 100%, 70%, 0%);

	--link-txt-color-current-hover: hsl(${THEME_HUE}, 100%, 95%);
	--link-udl-color-current-hover: hsla(${THEME_HUE}, 100%, 70%, 90%);

	a {
		font-weight: 500;
		transition: color 0.3s ease;

		box-sizing: border-box;
		padding: 0.5rem 0;
	}

	@media ${MOBILE_MEDIA_QUERY} {
		a {
			padding: 1rem 2rem;
			width: 100%;
			text-align: center;
			border-bottom: 1px solid rgba(255, 255, 255, 0.1);
			display: block;

			&:last-child {
				border-bottom: none;
			}
		}
	}
`;

export const BlogNavLinkColors = {
	yellow: {
		text: 'hsl(51, 100%, 75%)',
		shadow: '-2px -2px 12px #317c2a',
	},
	green: {
		text: '#9dff94',
		shadow: '2px 2px 18px hsl(51, 100%, 36%)',
	},
} as const;

export const BlogLinkNavTransitionDuration = '0.6s';
export const BlogNavLink = styled(NavLink)`
	a {
		color: ${BlogNavLinkColors.yellow.text};
		text-shadow: -2px -2px 12px ${BlogNavLinkColors.yellow.shadow};
		font-weight: 600;

		&:hover {
			color: ${BlogNavLinkColors.green.text};
			text-shadow: ${BlogNavLinkColors.green.shadow};
			animation: none;
		}

		transition-duration: ${BlogLinkNavTransitionDuration};
		transition-property: color, text-shadow;

		animation: blogNavLinkAnimation 2.4s ease-in-out infinite ${BlogLinkNavTransitionDuration};
	}

	@keyframes blogNavLinkAnimation {
		0% {
			color: ${BlogNavLinkColors.yellow.text};
			text-shadow: -2px -2px 12px ${BlogNavLinkColors.yellow.shadow};
		}
		50% {
			color: color-mix(in oklab, ${BlogNavLinkColors.yellow.text} 40%, ${BlogNavLinkColors.green.text} 50%);
			text-shadow: 2px 2px 18px color-mix(in oklab, ${BlogNavLinkColors.yellow.shadow} 40%, ${BlogNavLinkColors.green.shadow} 50%);
		}
		100% {
			color: ${BlogNavLinkColors.yellow.text};
			text-shadow: -2px -2px 12px ${BlogNavLinkColors.yellow.shadow};
		}
	}

	@media ${MOBILE_MEDIA_QUERY} {
		a {
			padding: 1rem 2rem;
			width: 100%;
			text-align: center;
			border-bottom: 1px solid rgba(255, 255, 255, 0.1);
			display: block;

			&:last-child {
				border-bottom: none;
			}
		}
	}
`;
